import React, { useState, useRef, useEffect, useContext, useCallback } from 'react'
import { connect } from 'react-redux'
import styles from './add-guests.scss'
import HeaderBar from 'src/components/HeaderBar/HeaderBar'
import addGuestsContext from '../addGuestsContext'
import InputGuestIcon from 'assets/icons/input-guest.svg'
import RightIcon from 'assets/icons/right-icon.png'
import PropTypes from 'prop-types'
import CommunityGroup from '../../../Components/CommunityGroup/CommunityGroup'
import Button from 'src/components/common/Button'
import CommunitySkeleton from './CommunitySkeleton/CommunitySkeleton'
import SearchPanel from './SearchPanel/SearchPanel'
import cn from 'classnames'
import UploadFile from 'assets/icons/upload-file.png'
import CSVFileUpload from '../../../Components/CSVFileUpload/CSVFileUpload'
import PreviewAddGuestsList from '../../../Components/PreviewAddGuestsList/PreviewAddGuestsList'
import { POPUP_BOX_TYPES_CONSTANTS } from 'src/constants'
import SvgIcon from 'src/components/common/SvgIcon'
import Tour from 'src/components/common/Tour/Tour'
import { TOUR_STEPS, getTourState, setTourFlag } from 'src/services/tour_storage_utils'

const AddGuests = ({
  communityList,
  eventList,
  loadingCompleted,
  onSaveGuests,
  addedMembers,
  onMemberChecked,
  onGroupChecked
}) => {
  const context = useContext(addGuestsContext)
  const [isDisableScroll, setIsDisableScroll] = useState(false)
  const [isOpenCSVUploadModal, setIsOpenCSVUploadModal] = useState(false)
  const [previewAddGuestsList, setPreviewAddGuestsList] = useState([])
  const [showTour, setShowTour] = useState(false)
  const uploadFileRef = useRef(null)
  const stepConfig = TOUR_STEPS.find(step => step.key === 'addGuests')
  const tourState = getTourState()
  const showAddGuestsTour = !tourState.isAddGuestsTourOpened
  const isSkipped = tourState.isSkipped

  useEffect(() => {
    context.onChangePopupBox(POPUP_BOX_TYPES_CONSTANTS.addGuests)
    context.onChangePopupBoxCallBack(() => {
      if (previewAddGuestsList.length > 0) {
        setPreviewAddGuestsList([])
      } else {
        context.onChangeComponent('AddGuestsList')
      }
    })
  }, [])

  const handleSaveGuests = () => {
    if (onSaveGuests) onSaveGuests()
    history.back()
  }

  const handleGetGuestsFromCSV = guests => {
    if (guests.length > 0) {
      history.pushState(null, null, location.href)
    }
    setPreviewAddGuestsList(guests)
  }

  const visiblePreviewAddGuestsList = previewAddGuestsList.length > 0
  const event = context.event

  const handleUploadFileRef = useCallback((domElement) => {
    uploadFileRef.current = domElement
    if (domElement && !showTour) {
      setShowTour(true)
    }
  }, [showTour])

  const handleTourClose = () => {
    setTourFlag('isAddGuestsTourOpened', true)
    setShowTour(false)
  }

  if (!loadingCompleted) {
    return <CommunitySkeleton />
  }

  return (
    <div className={styles['add-guests-container']}>
      <HeaderBar
        width={40}
        closeIcon
        onBack={() => context.onChangeComponent('AddGuestsList')}
        right={
          <img
            src={UploadFile}
            width="22"
            onClick={() => setIsOpenCSVUploadModal(true)}
            ref={handleUploadFileRef}
          />
        }
      >
        <div className={styles['header-title']}>
          <span className={styles['title']}>add guests</span>
          <span className={styles['subtitle']}>{event.name}</span>
        </div>
      </HeaderBar>

      <div
        className={cn(styles['content-container'], {
          [styles['content-container-disableScroll']]: isDisableScroll
        })}
      >
        <SearchPanel
          onDisableScroll={setIsDisableScroll}
          onGroupChecked={onGroupChecked}
          onMemberChecked={onMemberChecked}
          communityList={communityList}
          eventList={eventList}
        />

        <div className={styles['group-wrapper']}>
          <div className={styles['group-title']}>input</div>
          <div
            className={styles['list-item']}
            onClick={() => context.onChangeComponent('InputGuests')}
          >
            <SvgIcon icon={InputGuestIcon} width={40} height={40} />
            <div className={styles['content']}>
              <span>Input Numbers or Emails</span>
              <span className={styles['text']}>Add people not in your contacts</span>
            </div>
            <img src={RightIcon} width="20" />
          </div>
        </div>

        <CommunityGroup
          communityList={communityList}
          eventList={eventList}
          onGroupChecked={onGroupChecked}
          onMemberChecked={onMemberChecked}
          addedMembers={addedMembers}
        />
      </div>

      <div className={styles['button-wrapper']}>
        <Button
          disabled={addedMembers.length === 0}
          className={styles['button-inner']}
          onClick={handleSaveGuests}
        >
          ADD
        </Button>
      </div>

      <CSVFileUpload
        visible={isOpenCSVUploadModal}
        onGetGuestsFromCSV={handleGetGuestsFromCSV}
        onClose={() => setIsOpenCSVUploadModal(false)}
      />

      {visiblePreviewAddGuestsList && (
        <PreviewAddGuestsList
          title="Import CSV File"
          previewAddGuestsList={previewAddGuestsList}
          fixed
        />
      )}

      <Tour
        open={showTour && showAddGuestsTour && !isSkipped}
        steps={[{ ...stepConfig, target: uploadFileRef, placement: 'bottom-right' }]}
        highlightPadding={[20, 12, 20, 12]}
        mask
        arrow
        showSkipButton={false}
        tourCardContentClassName={styles['tour-card-content']}
        tourTitleClassName={styles['tour-title']}
        onFinish={handleTourClose}
        onClose={handleTourClose}
      />
    </div>
  )
}

AddGuests.propTypes = {
  communityList: PropTypes.array,
  eventList: PropTypes.array,
  loadingCompleted: PropTypes.bool,
  onSaveGuests: PropTypes.func,
  addedMembers: PropTypes.array,
  onMemberChecked: PropTypes.func,
  onGroupChecked: PropTypes.func
}

function mapStateToProps(state) {
  return {
    addGuestsList: state.frontend.addGuestsList
  }
}

export default connect(mapStateToProps, null)(AddGuests)
